import { once } from 'ramda';

type HardwareInfo = ReturnType<typeof getGraphicsAdapterInfo> & {
    cpuCores: number | null;
    ramAmount: typeof navigator.deviceMemory | null;
};

/**
 * Gets user's GPU information
 * @returns {{gpuRenderer: ?string, gpuVendor: ?string}}
 */
function getGraphicsAdapterInfo(): { gpuRenderer: string | null; gpuVendor: string | null } {
    let gl: WebGLRenderingContext | null = null;
    try {
        const canvas =
            typeof document === 'undefined'
                ? new OffscreenCanvas(1, 1)
                : document.createElement('canvas');
        gl = canvas.getContext('webgl');
        if (!gl) throw new Error('no WebGL');

        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (!debugInfo) throw new Error('no debug info');

        const info = {
            gpuVendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) as string,
            gpuRenderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string,
        };
        // Otherwise, the context continues to live in memory and clogs the limit of contexts, causing the loss of previous
        gl.getExtension('WEBGL_lose_context')?.loseContext();

        return info;
    } catch (e) {
        if (gl) {
            gl.getExtension('WEBGL_lose_context')?.loseContext();
        }
        return { gpuVendor: null, gpuRenderer: null };
    }
}

/**
 * Checks if WebGL is supported by the browser
 * @param version
 * @param checkIfPerformant
 */
export function checkWebGLSupport(version: 1 | 2 = 1, checkIfPerformant = false): boolean {
    let gl: WebGLRenderingContext | WebGL2RenderingContext | null = null;
    try {
        const canvas = document.createElement('canvas');
        gl = canvas.getContext(version === 1 ? 'webgl' : 'webgl2', {
            failIfMajorPerformanceCaveat: checkIfPerformant,
        }) as WebGLRenderingContext | WebGL2RenderingContext | null;

        return Boolean(gl);
    } finally {
        gl?.getExtension('WEBGL_lose_context')?.loseContext();
    }
}

/**
 * Gets user's hardware information
 * @returns {{cpuCores: ?number, ramAmount: ?number, gpuRenderer: ?string, gpuVendor: ?string}}
 */
export const getHardwareInfo = once(
    () =>
        ({
            // Safari caps this to 8 at maximum
            cpuCores: typeof navigator !== 'undefined' ? navigator?.hardwareConcurrency : null,
            // is supported only in Chromium-based browsers
            ramAmount: typeof navigator !== 'undefined' ? navigator?.deviceMemory : null,
            ...getGraphicsAdapterInfo(),
        } as HardwareInfo)
);

/**
 * Detects is user's have Apple Silicon M1 chip
 * @returns {Boolean}
 */
export const isAppleSiliconChip = once((): boolean => {
    const { gpuRenderer } = getHardwareInfo();
    if (!gpuRenderer) return false;

    return gpuRenderer.toLowerCase().includes('apple');
});

/**
 * Detects user's hardware acceleration enabled
 * @returns {Boolean}
 */
export const getIsHardwareAccelerationEnabled = once((): boolean => {
    const types = ['intel', 'amd', 'radeon', 'nvidia', 'geforce', 'apple'];
    const { gpuRenderer } = getHardwareInfo();
    if (!gpuRenderer) return false;

    const renderer = gpuRenderer.toLowerCase();
    return types.some(type => renderer.includes(type));
});
